import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  iHasRangeColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import {
  extractXmlProps,
  parseXML,
  rewriteChildren,
  upsertChildren,
  xmlElementToString
} from '@lfw/roesberg.liveforms.core';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { formatISO8601WithOffset } from '../../../../utils/dates';
import { BASE_TEXT_COLOR_VARIANTS, getArgbToRgba, getHexOrRgbaToArgb, getTextColor } from '../../../../utils/colors';
import { DARK_THEME_NAME } from '../../types/editor-settings';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { CellAddress, TableRange } from './table-item-form.types';

type SupportedColumnDefinitions =
  | 'xs:boolean'
  | 'xs:int'
  | 'xs:integer'
  | 'xs:double'
  | 'xs:float'
  | 'xs:date'
  | 'xs:dateTime'
  | 'xs:string'
  | string;
type SupportedColumnControls = 'string' | 'integer' | 'float' | 'date' | 'bool' | 'unknown';

interface TableColumn {
  fieldName: string;
  format: string;
  label: string;
  width: number;
}

interface TableRow {
  [key: string]: string;
}

type CellColors = { columnIndex: number; rowIndex: number; color: string }[];

interface TableSettings {
  isVerticalOrientation: boolean;
  selectedDataColumnStartIndex: number;
  selectedDataRecordStartIndex: number;
  cellColors: CellColors;
}

interface TableItemProperties {
  columns: TableColumn[];
  rows: TableRow[];
  settings: TableSettings;
}

@Component({
  selector: 'lfe-table-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NgxMaskDirective],
  providers: [provideNgxMask()],
  templateUrl: './table-item-form.component.html',
  standalone: true,
  styleUrl: './table-item-form.component.css'
})
export class TableItemFormComponent
  extends iHasRangeColorControls(InteractiveItemComponent)<TableItemProperties>
  implements OnInit, OnDestroy
{
  readonly translateService = inject(LiveFormsTranslateLoaderService);

  private cellSubscriptions: Subscription[] = [];

  properties: InteractiveItemProperties & TableItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    columns: [],
    rows: [],
    settings: {
      isVerticalOrientation: true,
      selectedDataColumnStartIndex: 1,
      selectedDataRecordStartIndex: 0,
      cellColors: []
    }
  };

  private initialXml = '';
  private hasChanged = signal(false);

  /**
   * Control ids of boolean cells the user has never touched.
   *
   * The desktop stores such a cell as an absent element (`xs:boolean` with `minOccurs="0"`) and renders
   * it indeterminate. Writing `false` for it would silently answer a question nobody answered, so these
   * cells are omitted from the saved XML again.
   */
  private readonly indeterminateCells = new Set<string>();

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `table_${this.currentId()}`);

  getCellHtmlId(address: CellAddress): string {
    const [columnIndex, rowIndex] = address;
    const column = this.allDataColumns()[columnIndex];
    if (!column?.fieldName) {
      return `${this.htmlId()}_${rowIndex}_column-${columnIndex}`;
    }
    return `${this.htmlId()}_${rowIndex}_${column.fieldName}`;
  }

  selectedRange = signal<TableRange>([
    [0, 0],
    [0, 0]
  ]);

  settingsCellColor = signal<CellColors>([]);

  selectRange = (fromCell: CellAddress, toCell?: CellAddress): void => {
    const [fromColumnIndex, fromRowIndex] = fromCell;
    const [toColumnIndex, toRowIndex] = toCell || fromCell;
    this.selectedRange.set([
      [fromColumnIndex, fromRowIndex],
      [toColumnIndex, toRowIndex]
    ]);
  };

  selectColumn = (columnIndex: number): void => {
    this.selectRange([columnIndex, 0], [columnIndex, this.properties.rows.length - 1]);
  };

  selectCell = (cell: CellAddress): void => {
    this.selectRange(cell);
  };

  getCellBackgroundColor = (cellAddress: CellAddress): string => {
    const [columnIndex, rowIndex] = cellAddress;
    const cellDescriptor = this.settingsCellColor().find(
      (c) => c.columnIndex === columnIndex && c.rowIndex === rowIndex
    );

    return cellDescriptor?.color || '';
  };

  getCellStyle = (cellAddress: CellAddress): { backgroundColor: string; color: string } => {
    const bgColor = this.getCellBackgroundColor(cellAddress);
    const textColor = getTextColor(bgColor);
    return {
      backgroundColor: bgColor,
      color: textColor
    };
  };

  getCellInputClass = (cellAddress: CellAddress): { 'inverted-calendar-indicator'?: boolean } => {
    const bgColor = this.getCellBackgroundColor(cellAddress);
    const textColor = getTextColor(bgColor);
    const isDarkMode = this.settings().theme === DARK_THEME_NAME;
    const useInvertedCalendarIndicator = isDarkMode
      ? textColor === BASE_TEXT_COLOR_VARIANTS.black
      : textColor === BASE_TEXT_COLOR_VARIANTS.white;
    return { 'inverted-calendar-indicator': useInvertedCalendarIndicator };
  };

  override setRangeBackgroundColor = (color: string, range?: TableRange): void => {
    const [fromCell, toCell] = range || this.selectedRange();

    const [fromColumnIndex, fromRowIndex] = fromCell;
    const [toColumnIndex, toRowIndex] = toCell;

    for (let i = Math.min(fromColumnIndex, toColumnIndex); i <= Math.max(fromColumnIndex, toColumnIndex); i++) {
      for (let j = Math.min(fromRowIndex, toRowIndex); j <= Math.max(fromRowIndex, toRowIndex); j++) {
        this.setCellBackgroundColor([i, j], color);
      }
    }

    this.hasChanged.set(true);
  };

  setCellBackgroundColor = (cellAddress: CellAddress, color: string): void => {
    const [columnIndex, rowIndex] = cellAddress;
    const currentCellIndex = this.properties.settings.cellColors.findIndex(
      (c) => c.columnIndex === columnIndex && c.rowIndex === rowIndex
    );

    if (currentCellIndex >= 0) {
      this.properties.settings.cellColors = this.properties.settings.cellColors.filter(
        (_, i) => i !== currentCellIndex
      );
    }

    if (color) {
      this.properties.settings.cellColors.push({ columnIndex, rowIndex, color });
    }

    this.settingsCellColor.set([...this.properties.settings.cellColors]);
  };

  isAddressWithinSelectedRange = (columnIndex: number, rowIndex: number): boolean => {
    const [[fromColumn, fromRow], [toColumn, toRow]] = this.selectedRange();

    return fromColumn <= columnIndex && columnIndex <= toColumn && fromRow <= rowIndex && rowIndex <= toRow;
  };

  isCellOnTopOfSelectedRange = (rowIndex: number): boolean => {
    const [[_, fromRow]] = this.selectedRange();

    return rowIndex === fromRow;
  };

  isCellOnBottomOfSelectedRange = (rowIndex: number): boolean => {
    const [_, [_toColumn, toRow]] = this.selectedRange();

    return rowIndex === toRow;
  };

  isCellOnLeftOfSelectedRange = (columnIndex: number): boolean => {
    const [[fromColumn]] = this.selectedRange();
    return columnIndex === fromColumn;
  };

  isCellOnRightOfSelectedRange = (columnIndex: number): boolean => {
    const [_, [toColumn]] = this.selectedRange();
    return columnIndex === toColumn;
  };

  labelColumns = computed(() => {
    return this.properties.columns.slice(0, this.properties.settings.selectedDataColumnStartIndex);
  });

  allDataColumns = computed(() => {
    return this.properties.columns.slice(this.properties.settings.selectedDataColumnStartIndex);
  });

  columnTypes = new Map<string, SupportedColumnDefinitions>();

  numberOutputTransform = (value: string | number | null | undefined): string | number | null | undefined => {
    if (!value || typeof value !== 'string') return value;
    const separators = this.settings().numberFormat;
    return value
      .replace(new RegExp('\\' + separators.thousandsSeparator, 'g'), '')
      .replace(separators.decimalSeparator, '.');
  };

  // Convert Format string to NgxMask pattern
  getInputMask(columnField: string): string | null {
    const column = this.properties.columns.find((col) => col.fieldName === columnField);
    if (!column || !column.format) return null;

    const format = column.format;

    // Handle numeric formats like "0.0 mm", "0.00 kg", etc.
    const numericFormatMatch = format.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
    if (numericFormatMatch) {
      const [, numberPart] = numericFormatMatch;
      const decimalPlaces = numberPart.includes('.') ? numberPart.split('.')[1].length : 0;

      if (decimalPlaces > 0) {
        return `separator.${decimalPlaces}`;
      } else {
        return 'separator.0';
      }
    }

    return null;
  }

  // Get suffix from format (like "mm", "kg", etc.)
  getFormatSuffix(columnField: string): string {
    const column = this.properties.columns.find((col) => col.fieldName === columnField);
    if (!column || !column.format) return '';

    const format = column.format;
    const numericFormatMatch = format.match(/^(\d+(?:\.\d+)?)\s*(.+)$/);
    if (numericFormatMatch) {
      return ` ${numericFormatMatch[2]}`; // Add space before suffix
    }

    return '';
  }

  getControlType(columnField: string): SupportedColumnControls {
    const xmlType = this.columnTypes.get(columnField);
    switch (xmlType) {
      case 'xs:boolean':
        return 'bool';
      case 'xs:int':
      case 'xs:integer':
        return 'integer';
      case 'xs:double':
      case 'xs:float':
        return 'float';
      case 'xs:date':
      case 'xs:dateTime':
        return 'date';
      case 'xs:string':
        return 'string';
      default:
        console.warn('Control not yet implemented for', xmlType);
        return 'unknown';
    }
  }

  /**
   * `X` and `Y` in the stored `CellColors` are **zero-based**, like the loop indices the template uses.
   * Treating them as one-based shifted every colour one column left and one row up, so the first column
   * lost its colour and the last row was never painted.
   */
  getParsedCellColors(value: string): CellColors {
    try {
      const parsedValue = JSON.parse(value) as { X: number; Y: number; Color: string }[];
      return parsedValue.map((cellColor) => ({
        columnIndex: cellColor.X,
        rowIndex: cellColor.Y,
        color: getArgbToRgba(cellColor.Color)
      }));
    } catch (e) {
      console.warn('Error while parsing cell colors', e);
      return [];
    }
  }

  getSerializedCellColors(cellColors: CellColors): string {
    return JSON.stringify(
      cellColors.map((value) => ({
        X: value.columnIndex,
        Y: value.rowIndex,
        Color: getHexOrRgbaToArgb(value.color)
      }))
    );
  }

  getCellFormControl(rowIndex: number, columnField: string): FormControl {
    const controlId = `${this.currentId()}_${rowIndex}_${columnField}`;
    let control = this.form().get(controlId) as FormControl;

    if (!control) {
      const row = this.properties.rows[rowIndex];
      const controlType = this.getControlType(columnField);
      const maybeEnabled = controlType !== 'unknown';
      const cellValue = row && Object.prototype.hasOwnProperty.call(row, columnField) ? row[columnField] : '';
      let initialValue: string | number | boolean = maybeEnabled ? cellValue : 'N/A';

      if (controlType === 'bool') {
        // An absent element means the cell was never set - the desktop shows it as indeterminate. Only
        // a present element carries a real true/false.
        if (!row || !Object.prototype.hasOwnProperty.call(row, columnField)) {
          this.indeterminateCells.add(controlId);
        }
        initialValue = cellValue === 'true' || cellValue === '1';
      }

      // Convert ISO 8601 dates to HTML date input format (YYYY-MM-DD)
      if (controlType === 'date' && cellValue) {
        try {
          const date = new Date(cellValue);
          if (!Number.isNaN(date.getTime())) {
            initialValue = date.toISOString().split('T')[0];
          }
        } catch (e) {
          console.warn(`Invalid date format for ${columnField}:`, cellValue);
        }
      }

      const actualColumnIndex = this.properties.columns.findIndex((col) => col.fieldName === columnField);
      const isEnabled =
        maybeEnabled &&
        actualColumnIndex >= this.properties.settings.selectedDataColumnStartIndex &&
        rowIndex >= this.properties.settings.selectedDataRecordStartIndex;

      control = new FormControl({ value: initialValue, disabled: !isEnabled });
      const subscription = control.valueChanges.subscribe(() => {
        // Leaving the indeterminate state is one-way, like the desktop's nullable checkbox.
        this.indeterminateCells.delete(controlId);
        this.hasChanged.set(true);
      });
      this.cellSubscriptions.push(subscription);
      this.form().addControl(controlId, control);
    }

    return control;
  }

  /** True while a boolean cell still holds the desktop's "never answered" state. */
  isCellIndeterminate(rowIndex: number, columnField: string): boolean {
    return this.indeterminateCells.has(`${this.currentId()}_${rowIndex}_${columnField}`);
  }

  // Table date cells suppress the native picker glyph, so this button is the only pointer affordance.
  // Tapping the field itself still opens the picker on iOS and Android, which do not render one.
  openDatePicker(cellHtmlId: string): void {
    const input = document.getElementById(cellHtmlId) as HTMLInputElement | null;
    input?.showPicker();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.allDataColumns().forEach((column) => {
      this.properties.rows.forEach((_, rowIndex) => {
        this.getCellFormControl(rowIndex, column.fieldName);
      });
    });

    this.settingsCellColor.set(this.properties.settings.cellColors || []);
  }

  override ngOnDestroy(): void {
    this.cellSubscriptions.forEach((sub) => sub.unsubscribe());
    this.cellSubscriptions = [];
    super.ngOnDestroy();
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & TableItemProperties {
    this.initialXml = xmlElementToString(xml) || '';
    const props = extractXmlProps(xml);

    const customId = props['CustomID'] || '';
    const id = props['ID'] || '';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const tableXmlDataElement = Array.from(xml.children).find((child) => child.nodeName === 'TableXmlData');
    let columns: TableColumn[] = [];
    let rows: TableRow[] = [];
    const settings: TableSettings = {
      isVerticalOrientation: false,
      selectedDataColumnStartIndex: 0,
      selectedDataRecordStartIndex: 0,
      cellColors: []
    };

    if (tableXmlDataElement) {
      const newDataSetElement = Array.from(tableXmlDataElement.children).find(
        (child) => child.nodeName === 'NewDataSet'
      );

      if (newDataSetElement) {
        const schemaElement = Array.from(newDataSetElement.children).find((child) => child.nodeName === 'xs:schema');
        if (schemaElement) {
          const elements = Array.from(schemaElement.querySelectorAll('xs\\:element, element'));
          elements.forEach((element) => {
            const name = element.getAttribute('name');
            const type = element.getAttribute('type');
            if (name && type && name.startsWith('Column')) {
              this.columnTypes.set(name, type);
            }
          });
        }

        const metaDataElements = Array.from(newDataSetElement.children).filter(
          (child) => child.nodeName === 'MetaData'
        );
        columns = metaDataElements.map((meta) => {
          const metaProps = extractXmlProps(meta);
          return {
            fieldName: metaProps['FieldName'] || '',
            format: metaProps['Format'] || '',
            label: metaProps['Label'] || '',
            width: parseFloat(metaProps['Width'] || '100')
          };
        });

        const tableDataElements = Array.from(newDataSetElement.children).filter(
          (child) => child.nodeName === 'TableData'
        );
        rows = tableDataElements.map((tableData) => {
          const row: TableRow = {};
          Array.from(tableData.children).forEach((column) => {
            row[column.nodeName] = column.textContent || '';
          });
          return row;
        });

        const settingsElements = Array.from(newDataSetElement.children).filter(
          (child) => child.nodeName === 'SettingsData'
        );
        settingsElements.forEach((setting) => {
          const settingProps = extractXmlProps(setting);
          const parameter = settingProps['Parameter'];
          const value = settingProps['Value'];

          switch (parameter) {
            case 'IsVerticalOrientation':
              settings.isVerticalOrientation = value === 'True';
              break;
            case 'SelectedDataColumnStartIndex':
              settings.selectedDataColumnStartIndex = Number(value || '0');
              break;
            case 'SelectedDataRecordStartIndex':
              settings.selectedDataRecordStartIndex = Number(value || '0');
              break;
            case 'CellColors':
              settings.cellColors = this.getParsedCellColors(value);
          }
        });
      }
    }

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      isGenericRepeatItem,
      resultValue: '',
      resultUser: '',
      resultTime: '',
      columns,
      rows,
      settings
    };
  }

  private writeRowToElement(tableDataElement: Element, rowIndex: number): void {
    const rowProps: { [key: string]: string } = {};
    const omittedColumns: string[] = [];

    this.properties.columns.forEach((column) => {
      const controlId = `${this.currentId()}_${rowIndex}_${column.fieldName}`;
      const control = this.form().get(controlId) as FormControl;

      if (control !== null) {
        const controlType = this.getControlType(column.fieldName);
        let value = control.value;

        if (controlType === 'bool') {
          if (this.indeterminateCells.has(controlId)) {
            omittedColumns.push(column.fieldName);
            return;
          }
          // 'true'/'false' is what the desktop writes for xs:boolean.
          value = value ? 'true' : 'false';
        }

        if (controlType === 'date') {
          if (value) {
            const dateValue = new Date(value);
            dateValue.setUTCHours(0, 0, 0, 0);
            value = formatISO8601WithOffset(dateValue);
          } else {
            omittedColumns.push(column.fieldName);
            return;
          }
        }

        rowProps[column.fieldName] = String(value || '');
      }
    });

    const orderedFields = this.properties.columns.map((column) => column.fieldName);
    rewriteChildren(tableDataElement, orderedFields, rowProps, omittedColumns);
  }

  override getXML(): string {
    if (!this.initialXml) {
      return '';
    }

    if (!this.hasChanged()) {
      return this.initialXml;
    }

    const xml = parseXML(this.initialXml);
    const root = xml.documentElement;

    const tableXmlDataElement = Array.from(root.children).find((child) => child.nodeName === 'TableXmlData');
    if (!tableXmlDataElement) {
      return this.initialXml;
    }

    const newDataSetElement = Array.from(tableXmlDataElement.children).find((child) => child.nodeName === 'NewDataSet');
    if (!newDataSetElement) {
      return this.initialXml;
    }

    const tableDataElements = Array.from(newDataSetElement.children).filter((child) => child.nodeName === 'TableData');

    this.properties.rows.forEach((_, rowIndex) => {
      const tableDataElement = tableDataElements[rowIndex];
      if (tableDataElement) {
        this.writeRowToElement(tableDataElement, rowIndex);
      }
    });

    const settingsElements = Array.from(newDataSetElement.children).filter(
      (child) => child.nodeName === 'SettingsData'
    );
    settingsElements.forEach((setting) => {
      const settingProps = extractXmlProps(setting);
      const parameter = settingProps['Parameter'];

      switch (parameter) {
        case 'CellColors':
          upsertChildren(setting, { Value: this.getSerializedCellColors(this.settingsCellColor()) });
      }
    });

    return xmlElementToString(root) || '';
  }
}
